import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { DeleteIcon } from '@/components/icons'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import type { Project, ProjectSortColumn } from '@/types'
import { getIndustryLabel, PROJECT_DEFAULT_SORT_DIRECTIONS } from '@/types'
import { ProjectCollaborators } from './ProjectCollaborators'
import { EditProjectDialog } from './edit'
import { DeleteProjectDialog } from './DeleteProjectDialog'
import { SortableTableHead } from './SortableTableHead'

interface ProjectListViewProps {
  projects: Project[]
  sortBy: ProjectSortColumn | null
  sortDirection: 'asc' | 'desc'
  onSortChange: (column: ProjectSortColumn) => void
}

export function ProjectListView({ projects, sortBy, sortDirection, onSortChange }: ProjectListViewProps) {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const canEdit = hasPermission(Permissions.Host.Project.Update)
  const canDelete = hasPermission(Permissions.Host.Project.Delete)
  const showActions = canDelete || canEdit

  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)

  const handleEditClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation()
    setEditingProject(project)
  }

  const handleDeleteClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation()
    setProjectToDelete(project)
    setDeleteDialogOpen(true)
  }

  return (
    <>
      <div className="rounded-md border">
        <Table aria-label="Projects">
          <TableHeader>
            <TableRow>
              <SortableTableHead
                column="name"
                label="Project Name"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={PROJECT_DEFAULT_SORT_DIRECTIONS}
                onSortChange={onSortChange}
              />
              <SortableTableHead
                column="client"
                label="Client"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={PROJECT_DEFAULT_SORT_DIRECTIONS}
                onSortChange={onSortChange}
              className="w-[150px]"
              />
              <SortableTableHead
                  column="industry"
                  label="Industry"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  defaultDirections={PROJECT_DEFAULT_SORT_DIRECTIONS}
                  onSortChange={onSortChange}
                className="w-[150px]"
                />
              <SortableTableHead
                  column="type"
                  label="Type"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  defaultDirections={PROJECT_DEFAULT_SORT_DIRECTIONS}
                  onSortChange={onSortChange}
                className="w-[140px]"
                />
              <TableHead className="w-[140px]">Team Members</TableHead>
              {showActions && <TableHead className="w-[170px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => (
              <TableRow
                key={project.id}
                className="cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <TableCell>
                  <span className="font-medium">{project.name}</span>
                </TableCell>
                <TableCell>
                  {project.client ?? '-'}
                </TableCell>
                <TableCell>{getIndustryLabel(project.industry) || '-'}</TableCell>
                <TableCell>{project.type ?? '-'}</TableCell>
                <TableCell>
                  {project.teamMembers && project.teamMembers.length > 0 ? (
                    <ProjectCollaborators members={project.teamMembers} />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                {showActions && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="iconSm"
                          aria-label="Edit project"
                          onClick={(e) => handleEditClick(e, project)}
                        >
                          <Pencil className="h-4 w-4 text-action-icon" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="iconSm"
                          aria-label="Delete project"
                          onClick={(e) => handleDeleteClick(e, project)}
                        >
                          <DeleteIcon className="h-5 w-5 text-action-icon" />
                          <span className="sr-only">Delete project</span>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editingProject && (
        <EditProjectDialog
          project={editingProject}
          open={!!editingProject}
          onOpenChange={(open) => !open && setEditingProject(null)}
        />
      )}

      {projectToDelete && (
        <DeleteProjectDialog
          project={projectToDelete}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        />
      )}
    </>
  )
}
